import React from 'react';
import SupportHoursConfig from '../../../components/settings/SupportHoursConfig';
import { useMaintenanceMode } from '../../../context/MaintenanceContext';
import { useWebsiteMaintenance } from '../../../../website/context/WebsiteMaintenanceContext';
import { auth as adminAuth } from '../../../admin-auth/adminAppAuth';
import { useAdminPermissions } from '../../../hooks/useAdminPermissions';
import { ADMIN_PERMISSIONS } from '../../../constants/permissions';
import MagicLinkToggle from '../../../components/settings/security/MagicLinkToggle';
import { updateMyAdminProfile } from '../../../components/settings/security/magicLinkSettingsApi';
import { fetchAdminMe } from '../../../api/adminMeApi';

const SettingsSection = ({ title, children, defaultExpanded = false }) => {
  const [expanded, setExpanded] = React.useState(defaultExpanded);

  return (
    <section className="admin-settings-section">
      <button
        type="button"
        className="admin-settings-section-head"
        onClick={() => setExpanded(!expanded)}
        aria-expanded={expanded}
      >
        <strong>{title}</strong>
        <span aria-hidden="true">{expanded ? '▴' : '▾'}</span>
      </button>
      {expanded ? <div className="admin-settings-section-body">{children}</div> : null}
    </section>
  );
};

const Settings = () => {
  const { isMaintenanceMode, toggleMaintenanceMode } = useMaintenanceMode();
  const { isWebsiteMaintenance, toggleWebsiteMaintenance } = useWebsiteMaintenance();
  const { hasPermission } = useAdminPermissions();
  const [saved, setSaved] = React.useState(false);
  const [showPasswordForm, setShowPasswordForm] = React.useState(false);
  const [currentPassword, setCurrentPassword] = React.useState('');
  const [newPassword, setNewPassword] = React.useState('');
  const [confirmPassword, setConfirmPassword] = React.useState('');
  const [supportHoursOpen, setSupportHoursOpen] = React.useState(false);

  // Profile state
  const [admin, setAdmin] = React.useState(null);
  const [profileLoading, setProfileLoading] = React.useState(true);
  const [profileSaving, setProfileSaving] = React.useState(false);
  const [profileEditing, setProfileEditing] = React.useState(false);
  const [profileError, setProfileError] = React.useState('');
  const [profileSuccess, setProfileSuccess] = React.useState('');
  const [profileData, setProfileData] = React.useState({
    firstName: '',
    lastName: '',
    phone: '',
    title: '',
    department: '',
    bio: ''
  });

  // Profile helpers
  const getInitials = () => {
    const firstName = admin?.firstName || '';
    const lastName = admin?.lastName || '';
    const email = admin?.email || '';
    if (firstName && lastName) return `${firstName[0]}${lastName[0]}`.toUpperCase();
    if (firstName) return firstName[0].toUpperCase();
    if (email) return email[0].toUpperCase();
    return 'A';
  };

  const getDisplayName = () => {
    const firstName = admin?.firstName || '';
    const lastName = admin?.lastName || '';
    if (firstName && lastName) return `${firstName} ${lastName}`;
    if (firstName) return firstName;
    if (lastName) return lastName;
    return admin?.email || 'Admin User';
  };

  React.useEffect(() => {
    const fetchAdminProfile = async () => {
      try {
        const currentUser = adminAuth.currentUser;
        if (!currentUser) {
          setProfileLoading(false);
          return;
        }
        const adminData = await fetchAdminMe();
        setAdmin(adminData);
          setProfileData({
            firstName: adminData.firstName || '',
            lastName: adminData.lastName || '',
            phone: adminData.phone || '',
            title: adminData.title || '',
            department: adminData.department || '',
            bio: adminData.bio || ''
          });
      } finally {
        setProfileLoading(false);
      }
    };
    fetchAdminProfile();
  }, []);

  const handleProfileInputChange = (e) => {
    const { name, value } = e.target;
    setProfileData(prev => ({ ...prev, [name]: value }));
  };

  const handleProfileEdit = () => {
    setProfileEditing(true);
    setProfileError('');
    setProfileSuccess('');
  };

  const handleProfileCancel = () => {
    setProfileEditing(false);
    setProfileData({
      firstName: admin?.firstName || '',
      lastName: admin?.lastName || '',
      phone: admin?.phone || '',
      title: admin?.title || '',
      department: admin?.department || '',
      bio: admin?.bio || ''
    });
  };

  const handleProfileSave = async () => {
    try {
      setProfileSaving(true);
      const currentUser = adminAuth.currentUser;
      if (!currentUser) return;
      const updateData = { ...profileData, updatedAt: new Date() };
      await updateMyAdminProfile(profileData);
      setAdmin(prev => ({ ...prev, ...updateData }));
      setProfileEditing(false);
      setProfileSuccess('Profile updated successfully!');
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (e) {
      setProfileError('Failed to update profile');
    } finally {
      setProfileSaving(false);
    }
  };

  return (
    <div className="admin-page">
      {hasPermission(ADMIN_PERMISSIONS.ADMIN.MANAGE) ? (
        <SettingsSection title="App Settings" defaultExpanded>
          <div className="admin-settings-row">
            <div>
              <strong>App Maintenance Mode</strong>
              <p className="admin-muted">Temporarily restrict access for all users except admins</p>
            </div>
            <label className="admin-switch">
              <input type="checkbox" checked={isMaintenanceMode} onChange={toggleMaintenanceMode} />
              <span />
            </label>
          </div>
          <div className="admin-settings-row">
            <div>
              <strong>Website Maintenance Mode</strong>
              <p className="admin-muted">Temporarily restrict access to the public website</p>
            </div>
            <label className="admin-switch">
              <input type="checkbox" checked={isWebsiteMaintenance} onChange={toggleWebsiteMaintenance} />
              <span />
            </label>
          </div>
          <div className="admin-settings-row">
            <div>
              <strong>Support Hours</strong>
              <p className="admin-muted">Configure when support is available</p>
            </div>
            <button type="button" className="admin-btn admin-btn-secondary" onClick={() => setSupportHoursOpen(!supportHoursOpen)}>
              Configure
            </button>
          </div>
          {supportHoursOpen ? (
            <div style={{ marginTop: 16 }}>
              <SupportHoursConfig />
            </div>
          ) : null}
        </SettingsSection>
      ) : null}

      <SettingsSection title="Account Settings" defaultExpanded>
        <MagicLinkToggle
          value={admin?.requireMagicLink === true}
          method={admin?.twoFactorMethod === 'sms' ? 'sms' : 'email'}
          disabled={!adminAuth.currentUser}
          loading={profileLoading}
          onChange={(next) => setAdmin((prev) => ({ ...(prev || {}), requireMagicLink: next }))}
          onMethodChange={(nextMethod) => setAdmin((prev) => ({ ...(prev || {}), twoFactorMethod: nextMethod }))}
        />
        <div className="admin-settings-row">
          <div>
            <strong>Manage Password</strong>
            <p className="admin-muted">Update your account password</p>
          </div>
          <button type="button" className="admin-btn admin-btn-secondary" onClick={() => setShowPasswordForm(!showPasswordForm)}>
            Update Password
          </button>
        </div>
        {showPasswordForm ? (
          <div className="admin-form-stack" style={{ marginTop: 16 }}>
            <label>
              <span className="admin-field-label">Current Password</span>
              <input className="admin-field" type="password" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} />
            </label>
            <label>
              <span className="admin-field-label">New Password</span>
              <input className="admin-field" type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} />
            </label>
            <label>
              <span className="admin-field-label">Confirm New Password</span>
              <input className="admin-field" type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} />
            </label>
            <button type="button" className="admin-btn" onClick={() => setSaved(true)}>Save Changes</button>
          </div>
        ) : null}
      </SettingsSection>

      <SettingsSection title="Profile Settings" defaultExpanded>
        {profileLoading ? (
          <p className="admin-muted">Loading profile...</p>
        ) : (
          <>
            <div className="admin-settings-row">
              <div className="admin-row">
                <span className="user-profile-avatar user-profile-avatar--fallback" style={{ width: 44, height: 44, fontSize: '0.95rem' }}>
                  {getInitials()}
                </span>
                <div>
                  <strong>{getDisplayName()}</strong>
                  <p className="admin-muted">{admin?.title || 'Administrator'}</p>
                </div>
              </div>
              {!profileEditing ? (
                <button type="button" className="admin-btn admin-btn-secondary" onClick={handleProfileEdit}>Edit</button>
              ) : (
                <div className="admin-row">
                  <button type="button" className="admin-btn admin-btn-secondary" onClick={handleProfileCancel} disabled={profileSaving}>Cancel</button>
                  <button type="button" className="admin-btn" onClick={handleProfileSave} disabled={profileSaving}>
                    {profileSaving ? 'Saving...' : 'Save'}
                  </button>
                </div>
              )}
            </div>

            {profileError ? <p className="admin-alert">{profileError}</p> : null}
            {profileSuccess ? <p className="admin-success-text">{profileSuccess}</p> : null}

            <div className="admin-form-stack">
              <label>
                <span className="admin-field-label">First Name</span>
                <input className="admin-field" name="firstName" value={profileData.firstName} onChange={handleProfileInputChange} disabled={!profileEditing} />
              </label>
              <label>
                <span className="admin-field-label">Last Name</span>
                <input className="admin-field" name="lastName" value={profileData.lastName} onChange={handleProfileInputChange} disabled={!profileEditing} />
              </label>
              <label>
                <span className="admin-field-label">Email</span>
                <input className="admin-field" value={admin?.email || ''} disabled />
              </label>
              <label>
                <span className="admin-field-label">Phone Number</span>
                <input className="admin-field" name="phone" value={profileData.phone} onChange={handleProfileInputChange} disabled={!profileEditing} />
              </label>
              <label>
                <span className="admin-field-label">Job Title</span>
                <input className="admin-field" name="title" value={profileData.title} onChange={handleProfileInputChange} disabled={!profileEditing} />
              </label>
              <label>
                <span className="admin-field-label">Department</span>
                <input className="admin-field" name="department" value={profileData.department} onChange={handleProfileInputChange} disabled={!profileEditing} />
              </label>
              <label>
                <span className="admin-field-label">Member Since</span>
                <input className="admin-field" value={admin?.createdAt?.toDate?.()?.toLocaleDateString() || 'N/A'} disabled />
              </label>
              <label>
                <span className="admin-field-label">Bio</span>
                <textarea className="admin-field" name="bio" rows={3} value={profileData.bio} onChange={handleProfileInputChange} disabled={!profileEditing} placeholder="Tell us about yourself..." />
              </label>
            </div>
          </>
        )}
      </SettingsSection>

      {saved ? <div className="admin-toast">Settings saved successfully!</div> : null}
    </div>
  );
};

export default Settings; 